import { ConsentAction } from "../Generated/ConsentAction";
import { ConsentPromptKind } from "../Generated/ConsentPromptKind";
import { ConsentStatisticsTransport } from "./ConsentStatisticsTransport";

const pendingSessionKey = "smartguide.consent-statistics.pending.v1";
const idleTimeout = 30 * 60 * 1000;
interface ConsentSession { promptId: string; lastActivityAt: number; shown: boolean; }

export class ConsentStatistics {
    private readonly transport: ConsentStatisticsTransport;
    private session: ConsentSession | null = null;
    private kind = ConsentPromptKind.Initial;
    private active = false;
    private decided = false;
    private requestedShown = false;
    private frame: number | null = null;

    constructor(private readonly documentRef: Document, private readonly windowRef: Window, private readonly overlay: HTMLElement) {
        this.transport = new ConsentStatisticsTransport(windowRef);
    }

    open(kind: ConsentPromptKind): void {
        if (this.active) return;
        this.kind = kind;
        this.decided = false;
        this.requestedShown = false;
        this.session = kind === ConsentPromptKind.Initial ? this.readSession() : null;
        this.active = true;
        this.documentRef.addEventListener("visibilitychange", this.scheduleShown);
        this.documentRef.addEventListener("prerenderingchange", this.scheduleShown);
        this.documentRef.addEventListener("pointerdown", this.touchSession);
        this.documentRef.addEventListener("keydown", this.touchSession);
        this.scheduleShown();
    }

    choose(action: ConsentAction.Accepted | ConsentAction.Rejected): void {
        if (!this.active || this.decided || !this.visible()) return;
        this.recordShown();
        if (!this.session) return;
        this.decided = true;
        void this.transport.send(this.report(action));
        if (this.kind === ConsentPromptKind.Initial) {
            try { this.windowRef.sessionStorage.removeItem(pendingSessionKey); } catch { /* Storage is optional. */ }
        }
        this.disconnect();
    }

    disconnect(): void {
        this.active = false;
        if (this.frame !== null) this.windowRef.cancelAnimationFrame(this.frame);
        this.frame = null;
        this.documentRef.removeEventListener("visibilitychange", this.scheduleShown);
        this.documentRef.removeEventListener("prerenderingchange", this.scheduleShown);
        this.documentRef.removeEventListener("pointerdown", this.touchSession);
        this.documentRef.removeEventListener("keydown", this.touchSession);
    }

    private readonly scheduleShown = (): void => {
        if (!this.visible() || this.frame !== null) return;
        this.frame = this.windowRef.requestAnimationFrame(() => { this.frame = null; this.recordShown(); });
    };

    private visible(): boolean {
        return this.active && this.overlay.isConnected && !this.overlay.hidden && this.documentRef.visibilityState === "visible"
            && !(this.documentRef as Document & { prerendering?: boolean }).prerendering;
    }

    private recordShown(): void {
        if (!this.visible()) return;
        const now = Date.now();
        if (this.session && now - this.session.lastActivityAt > idleTimeout) { this.session = null; this.requestedShown = false; }
        if (!this.session) {
            try {
                const bytes = this.windowRef.crypto.getRandomValues(new Uint8Array(16));
                this.session = { promptId: Array.from(bytes, value => value.toString(16).padStart(2, "0")).join(""), lastActivityAt: now, shown: false };
            } catch { return; }
        }
        this.touchSession();
        if (this.session.shown || this.requestedShown) return;
        this.requestedShown = true;
        const session = this.session;
        void this.transport.send(this.report(ConsentAction.Shown)).then(sent => {
            if (sent) { session.shown = true; if (this.session === session && !this.decided) this.saveSession(); }
        });
    }

    private report(action: ConsentAction) {
        const language = this.documentRef.documentElement.lang;
        return { version: 1, promptId: this.session!.promptId, action, promptKind: this.kind,
            uiLanguage: language.length <= 24 && /^[a-z]{2,3}(?:-[a-zA-Z0-9]{2,8}){0,2}$/.test(language) ? language : "en" };
    }

    private readonly touchSession = (): void => {
        if (!this.session || this.decided) return;
        if (Date.now() - this.session.lastActivityAt > idleTimeout) {
            this.session = null;
            this.requestedShown = false;
            this.recordShown();
            return;
        }
        this.session.lastActivityAt = Date.now();
        this.saveSession();
    };

    private saveSession(): void {
        if (this.kind !== ConsentPromptKind.Initial || !this.session) return;
        try { this.windowRef.sessionStorage.setItem(pendingSessionKey, JSON.stringify(this.session)); } catch { /* Document-local measurement still works. */ }
    }

    private readSession(): ConsentSession | null {
        try {
            const raw = this.windowRef.sessionStorage.getItem(pendingSessionKey);
            if (!raw || raw.length > 256) return null;
            const value = JSON.parse(raw) as ConsentSession;
            const age = Date.now() - value.lastActivityAt;
            return /^[a-f0-9]{32}$/.test(value.promptId) && typeof value.shown === "boolean" && Number.isFinite(age) && age >= 0 && age <= idleTimeout ? value : null;
        } catch { return null; }
    }
}
